import { motion } from 'framer-motion';
import { BubbleDto } from '../../store/saved-chats/savedChatsDto';
import VerticalScrollContainer from '../base/VerticalScrollContainer';
import { getStringBetween, MOTION_KEYS } from '../../util';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentClient } from '../../store/client/clientSlice';
import ChatBubble, { ChatBubbleProps } from '../chat/bubble/ChatBubble';
import { Chip } from '@nextui-org/react';

export interface SavedChatBoxProps {
  messages: BubbleDto[];
  headerTitle: string;
}

export default function SavedChatBox({
  messages,
  headerTitle,
}: SavedChatBoxProps) {
  const currentClient = useAppSelector(selectCurrentClient);
  const [messageCount, setMessageCount] = useState(0);

  const [scrollResetSignal, setScrollResetSignal] = useState(0);
  const [scrollBottomSignal, setScrollBottomSignal] = useState(0);

  useEffect(() => {
    setScrollResetSignal(Date.now());
  }, [currentClient]);

  useEffect(() => {
    const n = messages.length;
    if (n === 0) {
      return;
    }
    if (messageCount < n) {
      setScrollBottomSignal(Date.now());
      setMessageCount(n);
    }
  }, [messages, messageCount]);

  const chatHeader = (
    <motion.div
      key="chat-header"
      variants={{
        [MOTION_KEYS.INITIAL]: { y: '-150%' },
        [MOTION_KEYS.ANIMATE]: { y: 0 },
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.INITIAL}
      className="flex flex-row items-center gap-2 p-2 border-b-1 backdrop-blur-md"
    >
      <div className="flex flex-row items-center gap-2 px-2">
        {headerTitle && (
          <p key={headerTitle} className="text-lg font-semibold">
            {headerTitle}
          </p>
        )}
      </div>
    </motion.div>
  );

  const emptyChat = 'Select from the list the Chat to review';

  const bubbles =
    messages.length > 0 ? (
      <motion.div
        key="bubbles"
        variants={{
          [MOTION_KEYS.INITIAL]: {},
          [MOTION_KEYS.ANIMATE]: {
            transition: { staggerChildren: 0.05, delayChildren: 0.1 },
          },
          [MOTION_KEYS.EXIT]: {
            transition: { staggerChildren: 0.05, staggerDirection: -1 },
          },
        }}
        initial={MOTION_KEYS.INITIAL}
        animate={MOTION_KEYS.ANIMATE}
        exit={MOTION_KEYS.EXIT}
        className="flex flex-col w-full gap-4"
      >
        {messages.map((m, i) => {
          const reasoningResponse = getStringBetween(
            m.content,
            '<<RESPONSE>>',
            '<</RESPONSE>>'
          );

          const props: ChatBubbleProps = {
            messageId: m.messageId,
            message: reasoningResponse === '' ? m.content : reasoningResponse,
            timestamp: m.original_created_at,
            // @ts-ignore: role is always of type MessageRole
            role: m.role,
            hue: m.role === 'assistant' ? 'assistant' : 'client',
          };

          return <ChatBubble key={i} {...props} />;
        })}
      </motion.div>
    ) : (
      <Chip
        size="sm"
        variant="flat"
        className="text-gray-600 self-center empty-chat"
      >
        {emptyChat}
      </Chip>
    );

  return (
    <VerticalScrollContainer
      topContent={chatHeader}
      resetSignal={scrollResetSignal}
      scrollToBottomSignal={scrollBottomSignal}
      chatbox="saved-chats"
    >
      <div className="flex flex-col w-full h-full pt-4 px-4 gap-4">
        {bubbles}
      </div>
    </VerticalScrollContainer>
  );
}
